import { Location } from '@angular/common';
import { DestroyRef, Service, computed, inject, signal } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, type Event } from '@angular/router';
import { Title } from '@angular/platform-browser';

/** Una página de la que se vino: adónde volver y cómo nombrarla. */
export interface Paso {
  url: string;
  titulo: string;
  /** Número de entrada del historial: si es la inmediata anterior, volver es `back()`. */
  pagina: number;
}

/** Claves propias en `history.state`. Conviven con las del router (`navigationId`). */
const CLAVE_MIGAS = 'faroMigas';
const CLAVE_PAGINA = 'faroPagina';

/**
 * `state` que se le pasa a un `routerLink` o a `navigate` para arrancar de cero:
 * el menú. Entrar por el menú no es "venir de" la página anterior.
 */
export const ESTADO_RAIZ = { faroRaiz: true } as const;

/** `state` para volver a la miga `indice`: se recupera su cadena, no se apila. */
const CLAVE_VOLVER = 'faroVolverA';

/**
 * Si a la entrada del historial cuyo `state` es `estado` se llegó volviendo a
 * una miga (`volver` con índice), no por un link ni por el menú. La usa la tabla
 * para recuperar su página cuando la vuelta no es un `back()`.
 */
export function llegoVolviendo(estado: Record<string, unknown>): boolean {
  return typeof estado[CLAVE_VOLVER] === 'number';
}

/** Más atrás que esto no se muestra: la miga más vieja deja de servir. */
const MAXIMO = 4;

/** Páginas que nunca son origen de nada. */
const SIN_ORIGEN = ['/login', '/sin-acceso', '/olvide-mi-clave', '/restablecer-clave'];

interface Pendiente {
  desde: Paso;
  cadena: readonly Paso[];
  popstate: boolean;
}

/**
 * **De dónde vino cada página.** Lo que alimenta las migas y la flecha de volver.
 *
 * A un detalle se llega desde muchos lados (el listado, el mapa, un evento, otro
 * detalle): la flecha no puede apuntar fija al listado. Este servicio mira cada
 * navegación y guarda la cadena de páginas previas **en el `history.state` de la
 * propia entrada**. Por eso sobrevive al F5 y al atrás/adelante del navegador,
 * que devuelven el `state` de cada entrada tal cual.
 *
 * Reglas:
 *  - Cambiar sólo query params (filtros, solapas) no es cambiar de página: la
 *    cadena se conserva, con o sin `replaceUrl`.
 *  - Ir a una página que ya está en la cadena la recorta ahí: listado → luminaria
 *    → nodo → la misma luminaria deja sólo "listado".
 *  - Entrar por el menú (`ESTADO_RAIZ`) la vacía.
 *
 * El nombre de cada miga es el que declara la página (`nombrar`, lo hace
 * `app-volver` con el nombre de la entidad) o, si no, el `title` de la ruta.
 */
@Service()
export class OrigenService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly title = inject(Title);

  private readonly _migas = signal<readonly Paso[]>([]);
  /** Las páginas previas, de la más vieja a la inmediata anterior. */
  readonly migas = this._migas.asReadonly();
  readonly anterior = computed(() => this._migas().at(-1));

  /**
   * Cuántas páginas muestran las migas en su propia cabecera (los detalles, con
   * `app-volver`). Si hay alguna, el shell no repite la barra.
   */
  private readonly _enCabecera = signal(0);
  readonly enCabecera = computed(() => this._enCabecera() > 0);

  private pagina = 0;
  private nombre?: { token: object; titulo: string };
  private pendiente?: Pendiente;
  private iniciado = false;

  /** Se llama una vez, al arrancar la app. */
  iniciar(destroyRef: DestroyRef): void {
    if (this.iniciado) return;
    this.iniciado = true;
    const sub = this.router.events.subscribe((e) => this.alEvento(e));
    destroyRef.onDestroy(() => sub.unsubscribe());
  }

  /** Lo usa la página para que su miga diga el nombre de la entidad. */
  nombrar(token: object, titulo: string | undefined): void {
    if (titulo) this.nombre = { token, titulo };
    else if (this.nombre?.token === token) this.nombre = undefined;
  }

  /** La página se va: su nombre no tiene que quedarle a la siguiente. */
  olvidar(token: object): void {
    if (this.nombre?.token === token) this.nombre = undefined;
  }

  registrarCabecera(): () => void {
    this._enCabecera.update((n) => n + 1);
    return () => this._enCabecera.update((n) => n - 1);
  }

  /**
   * Vuelve a la miga `indice` (por defecto, la anterior). Si es la entrada
   * inmediata del historial, es un `back()` y el navegador restaura el scroll y
   * no apila; si no, se navega recuperando la cadena que esa página tenía.
   *
   * Sin origen, va a `alternativa` (el listado de la entidad).
   */
  volver(alternativa: string | readonly unknown[], indice?: number, reemplazar = false): void {
    const migas = this._migas();
    const i = indice ?? migas.length - 1;
    const paso = migas[i];
    if (!paso) {
      const destino = Array.isArray(alternativa) ? alternativa : [alternativa];
      void this.router.navigate(destino, { replaceUrl: reemplazar });
      return;
    }
    if (paso.pagina === this.pagina - 1 && i === migas.length - 1) {
      this.location.back();
      return;
    }
    void this.router.navigateByUrl(paso.url, {
      state: { [CLAVE_VOLVER]: i },
      replaceUrl: reemplazar,
    });
  }

  private alEvento(e: Event): void {
    if (e instanceof NavigationStart) {
      // La URL real del navegador, no `router.url`: el mapa escribe su cámara
      // con `Location.replaceState` sin pasar por el router.
      const url = this.location.path(true) || '/';
      this.pendiente = {
        desde: { url, titulo: this.nombre?.titulo ?? this.tituloDeRuta(), pagina: this.pagina },
        cadena: this._migas(),
        popstate: e.navigationTrigger === 'popstate',
      };
    } else if (e instanceof NavigationEnd) {
      this.alTerminar(e);
    }
  }

  private alTerminar(e: NavigationEnd): void {
    const p = this.pendiente;
    this.pendiente = undefined;
    const estado = (history.state ?? {}) as Record<string, unknown>;
    const extras = this.router.lastSuccessfulNavigation()?.extras;
    const pedido = (extras?.state ?? {}) as Record<string, unknown>;

    let cadena: readonly Paso[];
    if (!p || p.popstate || e.id === 1) {
      // Atrás/adelante o F5: la entrada trae su propia cadena.
      cadena = leerCadena(estado[CLAVE_MIGAS]);
      this.pagina = typeof estado[CLAVE_PAGINA] === 'number' ? (estado[CLAVE_PAGINA] as number) : 0;
    } else {
      if (!extras?.replaceUrl) this.pagina++;
      const volverA = pedido[CLAVE_VOLVER];
      if (pedido['faroRaiz']) {
        cadena = [];
      } else if (typeof volverA === 'number') {
        cadena = p.cadena.slice(0, volverA);
      } else if (mismaPagina(p.desde.url, e.urlAfterRedirects)) {
        cadena = p.cadena;
      } else if (SIN_ORIGEN.includes(ruta(p.desde.url)) || p.desde.url === '/') {
        cadena = [];
      } else {
        cadena = recortar([...p.cadena, p.desde], e.urlAfterRedirects);
      }
    }

    this._migas.set(cadena);
    history.replaceState({ ...estado, [CLAVE_MIGAS]: cadena, [CLAVE_PAGINA]: this.pagina }, '');
  }

  private tituloDeRuta(): string {
    return this.title.getTitle().replace(/\s*·\s*FARO$/, '') || 'Inicio';
  }
}

function ruta(url: string): string {
  return url.split(/[?#]/)[0];
}

function mismaPagina(a: string, b: string): boolean {
  return ruta(a) === ruta(b);
}

/** Si el destino ya está en la cadena, se vuelve a ese punto; y se limita el largo. */
function recortar(cadena: readonly Paso[], destino: string): readonly Paso[] {
  const i = cadena.findIndex((p) => mismaPagina(p.url, destino));
  const recortada = i >= 0 ? cadena.slice(0, i) : cadena;
  return recortada.slice(-MAXIMO);
}

function leerCadena(valor: unknown): readonly Paso[] {
  if (!Array.isArray(valor)) return [];
  return valor.filter(
    (p): p is Paso =>
      !!p &&
      typeof p.url === 'string' &&
      typeof p.titulo === 'string' &&
      typeof p.pagina === 'number',
  );
}
